import i18n from 'i18next';
import { push } from 'redux-first-history';
import { all, call, fork, put, takeLeading } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import documentAuthService from '@app/libs/auth/documentAuthService';
import { isKakaoInAppBrowser } from '@app/libs/browserUserAgent';
import closeKakaoInAppBrowser from '@app/libs/closeKakaoInAppBrowser';
import embeddedService from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import workspaceService from '@app/libs/workspaceService';
import {
  DOCUMENT__DOCUMENT_PROGRESS_REJECT_REQUEST,
  DOCUMENT__EMAIL_REQUEST,
  DOCUMENT__SIGNING_CANCEL_REQUEST,
} from '@app/store/actionTypes';
import {
  cancelSigning,
  cancelSigningFailure,
  cancelSigningSuccess,
  emailDocument,
  emailDocumentFailure,
  emailDocumentSuccess,
  rejectDocument,
  rejectDocumentFailure,
  rejectDocumentSuccess,
} from '@app/store/document/actions';
import { modalOpen } from '@app/store/modal/actions';
import { postDocumentsForwardingEmail } from '@app/store/transport-layer/document/documents/forwarding/email';
import { postDocumentsParticipantsRejection } from '@app/store/transport-layer/document/documents/participants/rejection';
import { postDocumentsParticipantsSigningCancellation } from '@app/store/transport-layer/document/documents/participants/signing/cancellation';
import { setDocumentAccessable } from '@app/store/transport-layer/document/documents/signer-access';

import attachmentsSaga from './attachments/sagas';
import auditTrailFileSaga from './auditTrailFile/sagas';
import dataSaga from './data/sagas';
import fileSaga from './file/sagas';

/**
 * 문서 서명을 취소한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.participantId
 * @params {string} params.message
 * @params {bool} params.isDocumentBlock
 */
export function* cancelSigningSaga({
  payload: { documentId, participantId, message, isDocumentBlock },
}: ReturnType<typeof cancelSigning>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsParticipantsSigningCancellation, {
      documentId,
      message,
      participantId,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, { documentId, isAccessable: false, workspaceId });
    }
    yield put(cancelSigningSuccess());
    if (embeddedService.isEmbedded) {
      yield call(redirect, embeddedService.redirectUrl, {
        query: {
          documentId,
          eventType: 'document_signing_canceled',
        },
      });
    } else {
      const modalMessage = isDocumentBlock
        ? i18n.t('cancelDocument-cancelMessageWithBlock')
        : i18n.t('cancelDocument-cancelMessageWithoutBlock');
      yield put(
        modalOpen('CONFIRM', {
          title: i18n.t('cancelDocument-cancelTitle'),
          content: modalMessage,
          closeText: i18n.t('Global-confirm'),
        })
      );

      if (isKakaoInAppBrowser() && documentAuthService.isLinkUser()) {
        // 카카오 인앱 브라우저에서 서명요청을 거절, 취소한 경우 유저가 이후에 할 행동이 없으므로 브라우저를 종료합니다.
        if (documentAuthService.hasSavedAccessToken()) {
          documentAuthService.restoreTokens();
        }
        closeKakaoInAppBrowser();
      } else {
        yield put(push('/documents'));
      }
    }
  } catch (error) {
    yield put(cancelSigningFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '서명 취소 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

/**
 * 완료된 문서 뷰어에서 문서를 이메일로 전송 (이메일로 전송 기능)
 * @params {object}
 * @params {string} params.documentId - 전송할 문서의 아이디
 * @params {string} params.senderName - 발송자 이름
 * @params {string} params.recipientEmail - 수신자의 이메일
 */
export function* emailDocumentSaga({
  payload: { documentId, recipientEmail, senderName },
}: ReturnType<typeof emailDocument>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsForwardingEmail, {
      documentId,
      recipientEmail,
      senderName,
      workspaceId,
    });
    yield put(emailDocumentSuccess());
    yield put(
      modalOpen('CONFIRM', {
        title: i18n.t('emailDocumentSaga-completeModalTitle'),
        content: i18n.t('emailDocumentSaga-completeModalContent'),
      })
    );
  } catch (error) {
    yield put(emailDocumentFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '전송 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

/**
 * 문서 진행을 거절한다.
 * @params {object}
 * @params {string} params.documentId
 * @params {string} params.participantId
 * @params {string} params.message
 * @params {bool} params.isDocumentBlock
 */
export function* rejectDocumentSaga({
  payload: { documentId, participantId, message, isDocumentBlock, exitPath },
}: ReturnType<typeof rejectDocument>) {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    yield call(postDocumentsParticipantsRejection, {
      documentId,
      message,
      participantId,
      workspaceId,
    });
    if (isDocumentBlock) {
      yield call(setDocumentAccessable, { documentId, isAccessable: false, workspaceId });
    }
    yield put(rejectDocumentSuccess());
    const modalMessage = isDocumentBlock
      ? i18n.t('rejectDocument-rejectMessageWithBlock')
      : i18n.t('rejectDocument-rejectMessageWithoutBlock');
    yield put(
      modalOpen('CONFIRM', {
        title: i18n.t('rejectDocument-rejectTitle'),
        content: modalMessage,
        closeText: i18n.t('Global-confirm'),
      })
    );

    yield put(push(exitPath ?? '/documents'));
  } catch (error) {
    yield put(rejectDocumentFailure());
    yield put(
      modalOpen('CONFIRM', {
        title: '거절 실패',
        content: '거절이 실패되었습니다.\n다시 시도해 주세요.',
      })
    );
  }
}

export default function* saga() {
  yield all([
    fork(attachmentsSaga),
    fork(auditTrailFileSaga),
    fork(dataSaga),
    fork(fileSaga),
    takeLeading(DOCUMENT__SIGNING_CANCEL_REQUEST, cancelSigningSaga),
    takeLeading(DOCUMENT__EMAIL_REQUEST, emailDocumentSaga),
    takeLeading(DOCUMENT__DOCUMENT_PROGRESS_REJECT_REQUEST, rejectDocumentSaga),
  ]);
}
